// ============================================
// PUSH NOTIFICATION SERVICE - Capacitor + FCM
// ============================================

import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor, registerPlugin } from '@capacitor/core';
import axios from 'axios';

// Custom iOS plugin to get FCM token
interface FCMTokenPlugin {
  getToken(): Promise<{ token: string }>;
}
const FCMToken = registerPlugin<FCMTokenPlugin>('FCMToken');

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

class PushNotificationService {
  private initialized = false;
  private onNotificationCallback: ((notification: any) => void) | null = null;

  /**
   * Check if push notifications are supported (native only)
   */
  isSupported(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Initialize push notifications
   * Call this after user login
   */
  async initialize(): Promise<void> {
    if (!this.isSupported()) {
      console.log('[PUSH] Not a native platform, skipping push notification setup');
      return;
    }

    if (this.initialized) {
      console.log('[PUSH] Already initialized');
      return;
    }

    try {
      // Request permission
      const permResult = await PushNotifications.requestPermissions();
      
      if (permResult.receive === 'granted') {
        // Register with FCM
        await PushNotifications.register();
        console.log('[PUSH] Registration initiated');
      } else {
        console.log('[PUSH] Push notification permission denied');
        return;
      }

      // Listen for registration success
      PushNotifications.addListener('registration', async (token) => {
        console.log('[PUSH] Token received from Capacitor:', token.value.substring(0, 30) + '...');
        console.log('[PUSH] Token length:', token.value.length, 'Platform:', Capacitor.getPlatform());
        
        let fcmToken = token.value;
        
        // On iOS, Capacitor returns APNs token (64 hex chars)
        // We need to get FCM token from Firebase Messaging instead
        if (Capacitor.getPlatform() === 'ios' && token.value.length <= 64) {
          console.log('[PUSH] iOS detected - APNs token received, fetching FCM token...');
          try {
            // Wait a moment for Firebase to process the APNs token
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            // Try to get FCM token via native bridge
            const result = await this.getIOSFCMToken();
            if (result) {
              fcmToken = result;
              console.log('[PUSH] iOS FCM Token obtained:', fcmToken.substring(0, 30) + '...');
            } else {
              console.warn('[PUSH] Could not get FCM token on iOS, using APNs token');
            }
          } catch (err) {
            console.error('[PUSH] Error getting iOS FCM token:', err);
          }
        }
        
        // Send token to backend
        await this.registerTokenWithBackend(fcmToken);
      });

      // Listen for registration errors
      PushNotifications.addListener('registrationError', (error) => {
        console.error('[PUSH] Registration error:', error);
      });

      // Listen for push notifications received while app is in foreground
      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('[PUSH] Notification received in foreground:', notification);
        
        // Call the callback if set
        if (this.onNotificationCallback) {
          this.onNotificationCallback({
            title: notification.title,
            body: notification.body,
            data: notification.data,
            foreground: true
          });
        }
      });

      // Listen for notification tap (app opened from notification)
      PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        console.log('[PUSH] Notification tapped:', action);
        
        const data = action.notification.data;
        
        // Navigate to the appropriate route
        if (data?.route) {
          // Use window.location for navigation since we don't have router access here
          window.location.href = data.route;
        }
      });

      this.initialized = true;
      console.log('[PUSH] Push notifications initialized successfully');
    } catch (error) {
      console.error('[PUSH] Error initializing push notifications:', error);
    }
  }

  /**
   * Register FCM token with backend
   */
  private async registerTokenWithBackend(token: string): Promise<void> {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        console.warn('[PUSH] No access token, cannot register FCM token');
        return;
      }

      await axios.post(
        `${API_URL}/api/notifications/register-token`,
        { token },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Store token locally for reference
      localStorage.setItem('fcmToken', token);
      console.log('[PUSH] Token registered with backend');
    } catch (error: any) {
      console.error('[PUSH] Error registering token with backend:', error.message);
    }
  }

  /**
   * Remove FCM token (call on logout)
   */
  async removeToken(): Promise<void> {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        await axios.delete(
          `${API_URL}/api/notifications/remove-token`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`
            }
          }
        );
      }

      localStorage.removeItem('fcmToken');
      console.log('[PUSH] Token removed');
    } catch (error: any) {
      console.error('[PUSH] Error removing token:', error.message);
    }
  }

  /**
   * Set callback for when notifications are received in foreground
   */
  onNotification(callback: (notification: any) => void): void {
    this.onNotificationCallback = callback;
  }

  /**
   * Update push notification preference
   */
  async updatePreference(enabled: boolean): Promise<void> {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return;

      await axios.put(
        `${API_URL}/api/notifications/preferences`,
        { enabled },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
    } catch (error: any) {
      console.error('[PUSH] Error updating preference:', error.message);
    }
  }

  /**
   * Get FCM token on iOS via custom FCMToken plugin
   */
  private async getIOSFCMToken(): Promise<string | null> {
    try {
      const result = await FCMToken.getToken();
      return result?.token || null;
    } catch (error) {
      console.warn('[PUSH] Error getting FCM token via FCMToken plugin:', error);
      return null;
    }
  }

  /**
   * Cleanup listeners (call on app destroy)
   */
  async cleanup(): Promise<void> {
    if (!this.isSupported()) return;
    
    try {
      await PushNotifications.removeAllListeners();
      this.initialized = false;
      this.onNotificationCallback = null;
    } catch (error) {
      console.error('[PUSH] Error cleaning up:', error);
    }
  }
}

export const pushNotificationService = new PushNotificationService();
